import { useEffect, useState } from "react";
import { sendFeedback } from "../../services/feedbackService";
import cityService from "../../api/cityService";

import {
    TextInput,
    TextAreaInput,
    SelectBox,
    PrimaryButton,
} from "../ui/FormControls";

const EMPTY_FORM = {
    name: "",
    email: "",
    cityId: "",
    message: "",
};

export default function FeedbackForm() {
    const [form, setForm] = useState(EMPTY_FORM);

    const [cities, setCities] = useState([]);

    const [loadingCities, setLoadingCities] = useState(true);

    const [submitting, setSubmitting] = useState(false);

    const [success, setSuccess] = useState("");

    const [error, setError] = useState("");

    useEffect(() => {
        loadCities();
    }, []);

    const loadCities = async () => {
        try {
            setLoadingCities(true);

            const data = await cityService.fetchCities();

            setCities(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error(err);

            setError(
                "Unable to load cities."
            );
        } finally {
            setLoadingCities(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        setSuccess("");
        setError("");

        if (!form.name.trim()) {
            setError("Name is required.");
            return;
        }

        if (!form.email.trim()) {
            setError("Email is required.");
            return;
        }

        if (!form.cityId) {
            setError("Please select your city.");
            return;
        }

        if (!form.message.trim()) {
            setError("Message is required.");
            return;
        }

        try {
            setSubmitting(true);

            await sendFeedback({
                name: form.name,
                email: form.email,
                cityId: Number(form.cityId),
                message: form.message,
            });

            setSuccess(
                "Thank you! Your feedback has been submitted successfully."
            );

            setForm(EMPTY_FORM);
        } catch (err) {
            console.error(err);

            setError(
                err.message ||
                "Failed to submit feedback."
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <form
            onSubmit={handleSubmit}
            className="space-y-5"
        >
            {error && (
                <div className="rounded-xl border border-red-300 bg-red-50 p-4 text-red-700">
                    {error}
                </div>
            )}

            {success && (
                <div className="rounded-xl border border-green-300 bg-green-50 p-4 text-green-700">
                    {success}
                </div>
            )}

            <TextInput
                label="Full Name"
                placeholder="Your name"
                value={form.name}
                onChange={(e) =>
                    setForm((prev) => ({
                        ...prev,
                        name: e.target.value,
                    }))
                }
                required
            />

            <TextInput
                label="Email Address"
                type="email"
                placeholder="name@example.com"
                value={form.email}
                onChange={(e) =>
                    setForm((prev) => ({
                        ...prev,
                        email: e.target.value,
                    }))
                }
                required
            />

            <SelectBox
                label="City"
                value={form.cityId}
                onChange={(e) =>
                    setForm((prev) => ({
                        ...prev,
                        cityId: e.target.value,
                    }))
                }
                disabled={loadingCities}
                required
            >
                <option value="">
                    {loadingCities
                        ? "Loading cities..."
                        : "Select city"}
                </option>

                {cities.map((city) => (
                    <option
                        key={city.id}
                        value={city.id}
                    >
                        {city.name}
                    </option>
                ))}
            </SelectBox>

            <TextAreaInput
                label="Feedback"
                rows={6}
                placeholder="Write your feedback here..."
                value={form.message}
                onChange={(e) =>
                    setForm((prev) => ({
                        ...prev,
                        message: e.target.value,
                    }))
                }
                required
            />

            <PrimaryButton
                type="submit"
                disabled={submitting}
            >
                {submitting
                    ? "Submitting..."
                    : "Submit Feedback"}
            </PrimaryButton>

            <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 text-sm text-slate-600">
                Feedback can be submitted anonymously or while signed in.
                If you are authenticated, your account may automatically be
                linked to the feedback to help our support team respond more
                efficiently.
            </div>
        </form>
    );
}